/**
 * useStateMachine - Centralized State Machine for AI Assistant
 *
 * This composable provides a single source of truth for all UI state.
 * It uses a flat state enum and event-driven transitions.
 *
 * Key principles:
 * - One flat state (no nested reviewStatus)
 * - Events trigger transitions (not direct setters)
 * - Side effects handled via watchers in components
 * - Components use isIn() to determine visibility
 */

import { ref, readonly } from "vue";
import { useStatusTracking } from "./useStatusTracking";

// =============================================================================
// Types
// =============================================================================

/**
 * Single flat state enum covering the entire flow.
 * No nested states - each state maps to exactly one UI configuration.
 */
export type AIAssistantState =
  | "idle" // FAB visible, nothing active
  | "form_template_selection" // Template selection modal (browse & preview)
  | "device_selection" // Device selection modal open
  | "recording" // Recording modal (iframe) open
  | "processing" // Processing modal - waiting for summary
  | "review_ready" // Popover open with summary ready for review
  | "error"; // Error state

/**
 * Events that trigger state transitions.
 * All state changes go through send(event).
 */
export type AIAssistantEvent =
  | { type: "FAB_CLICK" }
  | { type: "EXISTING_SESSION"; session: SessionData; status: string }
  | { type: "SESSION_CREATED"; session: SessionData }
  | { type: "DEVICE_DESKTOP"; recorderUrl: string; isTabRecorder?: boolean }
  | { type: "CLOSE" }
  // WebSocket events (mapped from NOA recorderStatus)
  | { type: "WS_RECORDED" }
  | { type: "WS_SUMMARIZED"; summaryData: string; transcriptionData: string }
  | { type: "WS_VERIFIED" }
  | { type: "WS_ERROR"; error: string }
  // Device recording (mobile started recording while in device_selection or idle)
  | { type: "WS_RECORDING" }
  // User actions
  | { type: "START_NEW" }
  | { type: "FETCH_SUMMARY_SUCCESS"; summaryData: string; transcriptionData: string };

/**
 * Session data from API
 */
export interface SessionData {
  sessionId?: string;
  recorderSessionId?: string;
  appointmentId?: string;
  licenseId?: string | number;
  patientId?: string | number;
  professionalId?: string | number;
  userId?: string | number;
  pms?: string;
  transcription?: string | string[];
  transcriptionData?: string;
  summaryData?: string;
  parsedSummary?: string;
  reviewedSummary?: string;
  transcriptionStatus?: string;
  recorderStatus?: string;
  status?: string;
  qrCode?: string;
  recorderUrl?: string;
  returnUrl?: string;
  originalUrl?: string;
  templateId?: string | number;
  template?: {
    id: number;
    name: string;
    description: string;
    sections?: Record<string, unknown>;
  };
  [key: string]: unknown;
}

/**
 * Template for selection
 */
export interface Template {
  id: number;
  description: string;
  name?: string;
  sections?: unknown[];
}

/**
 * State history entry for debugging
 */
interface HistoryEntry {
  id: number;
  timestamp: number;
  fromState: AIAssistantState;
  toState: AIAssistantState;
  event: string;
  data?: Record<string, unknown>;
}

// =============================================================================
// Session Lock (integrated from useSessionLock)
// =============================================================================

const SESSION_LOCK_KEY = "aiAssistant.lockedSessionId";
const SESSION_CONTEXT_KEY = "aiAssistant.lockedSessionContext";
const SESSION_PAGE_LOAD_KEY = "aiAssistant.pageLoadId";

const currentPageLoadId = Math.random().toString(36).substring(2, 15);

function isPageRefresh(): boolean {
  const storedPageLoadId = sessionStorage.getItem(SESSION_PAGE_LOAD_KEY);
  if (storedPageLoadId !== currentPageLoadId) {
    sessionStorage.setItem(SESSION_PAGE_LOAD_KEY, currentPageLoadId);
    return true;
  }
  return false;
}

// Clear stale lock on page refresh
if (isPageRefresh()) {
  sessionStorage.removeItem(SESSION_LOCK_KEY);
  sessionStorage.removeItem(SESSION_CONTEXT_KEY);
}

// =============================================================================
// Singleton State (Module Level)
// =============================================================================

/** Current state */
const state = ref<AIAssistantState>("idle");

/** Current session data */
const sessionData = ref<SessionData | null>(null);

/** Cached templates */
const templates = ref<Template[]>([]);

/** Recorder iframe URL */
const recorderUrl = ref<string>("");

/** Error message (when in error state) */
const errorMessage = ref<string>("");

/** Summary saving */
const summarySaving = ref<boolean>(false);

/** Session lock */
const lockedSessionId = ref<string | null>(sessionStorage.getItem(SESSION_LOCK_KEY));

/** Whether recording is active on device (mobile) while in device_selection or idle */
const deviceRecordingActive = ref(false);

/** Whether the recorder was opened in another browser tab (prevents mobile widget from firing on WS_RECORDING) */
const recorderOpenedInTab = ref(false);

/** Whether the current recording state was recovered from an existing session (tab ref lost) */
const isRecoveredSession = ref(false);

/** Transition history for debugging */
const history = ref<HistoryEntry[]>([]);
let historyIdCounter = 0;
const MAX_HISTORY = 50;

// =============================================================================
// UI State (Module Level)
// =============================================================================

/** UI display mode - widget (popover) or modal (full dialog) */
const displayMode = ref<"widget" | "modal">("widget");

/** UI minimized state - soft hide that preserves underlying state */
const minimized = ref(false);

/** Recorder modal visibility - can be hidden while still recording */
const recorderVisible = ref(true);

// =============================================================================
// Transition Logic
// =============================================================================

/**
 * Valid transitions map.
 * Key: current state, Value: array of valid target states
 */
const VALID_TRANSITIONS: Record<AIAssistantState, AIAssistantState[]> = {
  idle: ["form_template_selection", "processing", "review_ready", "error"],
  form_template_selection: ["device_selection", "idle"],
  device_selection: ["recording", "processing", "review_ready", "idle", "error"],
  recording: ["processing", "idle"],
  processing: ["review_ready", "error"],
  review_ready: ["idle", "form_template_selection"],
  error: ["idle", "form_template_selection"],
};

/**
 * Check if a transition is valid
 */
function canTransition(from: AIAssistantState, to: AIAssistantState): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/** Add entry to transition history */
// prettier-ignore
function addHistoryEntry(from: AIAssistantState, to: AIAssistantState, eventType: string, data?: Record<string, unknown>) {
  history.value.push({
    id: ++historyIdCounter,
    timestamp: Date.now(),
    fromState: from,
    toState: to,
    event: eventType,
    data: data,
  });

  if (history.value.length > MAX_HISTORY) {
    history.value = history.value.slice(-MAX_HISTORY);
  }
}

// =============================================================================
// Session Lock Functions
// =============================================================================

function lockSession(sessionId: string, context?: { patientId: number | string; licenseId: number | string }) {
  lockedSessionId.value = sessionId;
  sessionStorage.setItem(SESSION_LOCK_KEY, sessionId);
  sessionStorage.setItem(SESSION_PAGE_LOAD_KEY, currentPageLoadId);

  if (context) {
    sessionStorage.setItem(SESSION_CONTEXT_KEY, JSON.stringify(context));
  }
}

function unlockSession() {
  lockedSessionId.value = null;
  sessionStorage.removeItem(SESSION_LOCK_KEY);
  sessionStorage.removeItem(SESSION_CONTEXT_KEY);
}

function isContextMatch(patientId: number | string, licenseId: number | string): boolean {
  const storedContext = sessionStorage.getItem(SESSION_CONTEXT_KEY);
  if (!storedContext) return true;

  try {
    const ctx = JSON.parse(storedContext);
    return Number(ctx.patientId) === Number(patientId) && Number(ctx.licenseId) === Number(licenseId);
  } catch {
    return true;
  }
}

function shouldBlockSessionCreation(patientId: number | string, licenseId: number | string): boolean {
  if (!lockedSessionId.value) return false;
  if (!isContextMatch(patientId, licenseId)) {
    unlockSession();
    return false;
  }
  return true;
}

// =============================================================================
// Main Composable
// =============================================================================

export function useStateMachine() {
  // ---------------------------------------------------------------------------
  // State Helpers
  // ---------------------------------------------------------------------------

  /**
   * Check if current state matches any of the given states.
   * Used for component visibility.
   *
   * @example isIn('processing', 'review_ready')
   */
  function isIn(...states: AIAssistantState[]): boolean {
    return states.includes(state.value);
  }

  // ---------------------------------------------------------------------------
  // Event Handler (Main Entry Point)
  // ---------------------------------------------------------------------------

  /**
   * Send an event to trigger a state transition.
   * This is the only way to change state.
   */
  async function send(event: AIAssistantEvent): Promise<void> {
    const prevState = state.value;

    switch (event.type) {
      // -----------------------------------------------------------------------
      // User Initiated Events
      // -----------------------------------------------------------------------

      case "FAB_CLICK":
        if (prevState === "idle") {
          state.value = "form_template_selection";
          addHistoryEntry(prevState, "form_template_selection", "FAB_CLICK");
        }
        // For states where FAB is visible (recording, processing, review_ready, error),
        // FAB_CLICK is used to restore/toggle the popover visibility.
        // The state doesn't change - components handle visibility via displayMode.
        else if (["recording", "processing", "review_ready", "error"].includes(prevState)) {
          addHistoryEntry(prevState, prevState, "FAB_CLICK_RESTORE");
        }
        break;

      case "EXISTING_SESSION":
        if (prevState === "idle") {
          sessionData.value = event.session;
          const status = event.status.toLowerCase();

          // Lock the session
          if (event.session.recorderSessionId) {
            lockSession(event.session.recorderSessionId, {
              patientId: event.session.patientId || 0,
              licenseId: event.session.licenseId || 0,
            });
          }

          // Route based on status
          if (status === "summarized") {
            state.value = "review_ready";
            minimized.value = false;
            displayMode.value = "modal";
            addHistoryEntry(prevState, "review_ready", "EXISTING_SESSION", { status });
          } else if (
            ["recording", "recorded", "transcribing", "transcribed", "analizing", "processing"].includes(status)
          ) {
            // recording: session in progress in another tab (existing flow)
            // recorded/transcribing/transcribed/analizing: open recording FAB with link to recording tab
            if (event.session.recorderUrl) {
              recorderUrl.value = event.session.recorderUrl;
            }
            isRecoveredSession.value = true;
            recorderOpenedInTab.value = true;
            state.value = "recording";
            minimized.value = false;
            addHistoryEntry(prevState, "recording", "EXISTING_SESSION", { status });
          }
        }
        break;

      case "SESSION_CREATED":
        if (prevState === "form_template_selection") {
          sessionData.value = event.session;

          // Lock the session immediately
          const sessionIdToLock = event.session.recorderSessionId || event.session.sessionId;
          if (sessionIdToLock) {
            lockSession(sessionIdToLock, {
              patientId: event.session.patientId || 0,
              licenseId: event.session.licenseId || 0,
            });
          }

          state.value = "device_selection";
          addHistoryEntry(prevState, "device_selection", "SESSION_CREATED", {
            sessionId: sessionIdToLock,
          });
        }
        break;

      case "DEVICE_DESKTOP":
        if (prevState === "device_selection") {
          recorderUrl.value = event.recorderUrl;
          recorderOpenedInTab.value = event.isTabRecorder ?? false;
          deviceRecordingActive.value = false;
          state.value = "recording";
          addHistoryEntry(prevState, "recording", "DEVICE_DESKTOP");
        }
        break;

      case "CLOSE":
        // Close/minimize from modal states (form_template, device, recording, error) returns to idle
        // BUT: review_ready and processing should NOT be reset - just minimize the UI
        if (["form_template_selection", "device_selection", "recording", "error"].includes(prevState)) {
          unlockSession();
          deviceRecordingActive.value = false;
          isRecoveredSession.value = false;
          recorderOpenedInTab.value = false;
          state.value = "idle";
          addHistoryEntry(prevState, "idle", "CLOSE");
        }
        // NOTE: review_ready and processing states are NOT reset on CLOSE
        // User must explicitly discard (WS_VERIFIED) or the flow continues
        // This allows minimize/recover without losing progress
        break;

      case "START_NEW":
        if (prevState === "review_ready" || prevState === "error") {
          unlockSession();
          sessionData.value = null;
          recorderUrl.value = "";
          errorMessage.value = "";
          recorderOpenedInTab.value = false;
          state.value = "form_template_selection";
          addHistoryEntry(prevState, "form_template_selection", "START_NEW");
        }
        break;

      // -----------------------------------------------------------------------
      // WebSocket Events (mapped from NOA recorderStatus)
      // -----------------------------------------------------------------------

      case "WS_RECORDING":
        // Recording started on device (mobile) while still in device_selection or idle.
        // Skip if the recorder was opened in another tab — that flow handles its own UI via the FAB.
        if ((prevState === "device_selection" || prevState === "idle") && !recorderOpenedInTab.value) {
          deviceRecordingActive.value = true;
          state.value = "idle";
          addHistoryEntry(prevState, "idle", "WS_RECORDING");
        }
        break;

      case "WS_RECORDED":
        // Recording finished, move to processing
        if (prevState === "recording") {
          isRecoveredSession.value = false;
          state.value = "processing";
          addHistoryEntry(prevState, "processing", "WS_RECORDED");
        }
        break;

      case "WS_SUMMARIZED":
        if (prevState === "processing" || prevState === "device_selection" || prevState === "idle") {
          // Update session data with summary
          if (sessionData.value) {
            sessionData.value = {
              ...sessionData.value,
              summaryData: event.summaryData,
              transcriptionData: event.transcriptionData,
            };
          }
          deviceRecordingActive.value = false;
          state.value = "review_ready";
          displayMode.value = "modal";
          minimized.value = false; // Reset minimize for new review sessions
          addHistoryEntry(prevState, "review_ready", "WS_SUMMARIZED");
        }
        break;

      case "FETCH_SUMMARY_SUCCESS":
        // Similar to WS_SUMMARIZED but triggered by API fetch
        if (prevState === "processing" || prevState === "device_selection" || prevState === "idle") {
          if (sessionData.value) {
            sessionData.value = {
              ...sessionData.value,
              summaryData: event.summaryData,
              transcriptionData: event.transcriptionData,
            };
          }
          state.value = "review_ready";
          minimized.value = false; // Reset minimize for new review sessions
          addHistoryEntry(prevState, "review_ready", "FETCH_SUMMARY_SUCCESS");
        }
        break;

      case "WS_VERIFIED":
        // Session complete (user saved or discarded)
        if (prevState === "review_ready") {
          unlockSession();
          sessionData.value = null;
          recorderUrl.value = "";
          recorderOpenedInTab.value = false;
          state.value = "idle";
          addHistoryEntry(prevState, "idle", "WS_VERIFIED");
        }
        break;

      case "WS_ERROR":
        if (
          prevState === "processing" ||
          prevState === "recording" ||
          prevState === "device_selection" ||
          prevState === "idle"
        ) {
          deviceRecordingActive.value = false;
          errorMessage.value = event.error;
          state.value = "error";
          addHistoryEntry(prevState, "error", "WS_ERROR", { error: event.error });
        }
        break;

      default:
        // Unknown event type - no action
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // NOA Status Handler
  // ---------------------------------------------------------------------------

  /** Handle NOA recorderStatus updates from WebSocket. Maps NOA status values to state machine events. */
  // prettier-ignore
  function handleRecorderStatus(status: string, data?: { summaryData?: string; transcriptionData?: string; error?: string }): void {
    const normalizedStatus = status.toLowerCase();
    const sessionId = sessionData.value?.recorderSessionId || sessionData.value?.sessionId || null;
    
    // Track status for analytics (session-aware deduplication)
    const { trackStatus } = useStatusTracking();
    trackStatus(normalizedStatus, sessionId);
    
    console.log("[StateMachine:handleRecorderStatus] Processing status:", {
      rawStatus: status,
      normalizedStatus,
      currentState: state.value,
      data,
    });

    switch (normalizedStatus) {
      case "recording":
        // Recording started on device (e.g. mobile via QR code)
        if (state.value === "device_selection" || state.value === "idle") {
          send({ type: "WS_RECORDING" });
        }
        break;

      case "recorded":
      case "transcribing":
      case "transcribed":
        break;
      case "analyzing":
        // All these mean "processing" - only transition if coming from recording
        if (state.value === "recording") {
          send({ type: "WS_RECORDED" });
        }
        // Also handle when coming from device_selection (mobile flow)
        if (state.value === "idle") {
          send({ type: "WS_RECORDED" });
        }
        break;

      case "summarized":
        send({
          type: "WS_SUMMARIZED",
          summaryData: data?.summaryData || "",
          transcriptionData: data?.transcriptionData || "",
        });
        break;

      case "verified":
      case "discarded":
        send({ type: "WS_VERIFIED" });
        break;

      case "empty_summary":
        console.log("[StateMachine:handleRecorderStatus] EMPTY_SUMMARY detected! Sending WS_ERROR");
        send({ type: "WS_ERROR", error: "Não houve transcrição suficiente para gerar seu resumo." });
        break;

      case "error":
      case "interrupted":
        send({ type: "WS_ERROR", error: data?.error || "Processing failed" });
        break;

      default:
        // Unhandled status - no action
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Data Setters (for use by components)
  // ---------------------------------------------------------------------------

  /**
   * Set templates (from API fetch)
   */
  function setTemplates(newTemplates: Template[]): void {
    templates.value = newTemplates;
  }

  /**
   * Set summary saving (from API fetch)
   */
  function setSummarySaving(newSummarySaving: boolean): void {
    summarySaving.value = newSummarySaving;
  }
  /**
   * Get summary saving
   */
  function getSummarySaving(): boolean {
    return summarySaving.value;
  }

  /**
   * Update session data (merge with existing)
   */
  function updateSessionData(data: Partial<SessionData>): void {
    if (sessionData.value) {
      sessionData.value = { ...sessionData.value, ...data };
    } else {
      sessionData.value = data as SessionData;
    }
  }

  /**
   * Clear history (for DevPanel)
   */
  function clearHistory(): void {
    history.value = [];
    historyIdCounter = 0;
  }

  /**
   * Reset all state (full reset)
   */
  function reset(): void {
    const prevState = state.value;
    unlockSession();
    state.value = "idle";
    sessionData.value = null;
    templates.value = [];
    recorderUrl.value = "";
    errorMessage.value = "";
    displayMode.value = "widget";
    minimized.value = false;
    recorderVisible.value = true;
    deviceRecordingActive.value = false;
    recorderOpenedInTab.value = false;
    isRecoveredSession.value = false;
    addHistoryEntry(prevState, "idle", "RESET");
  }

  // ---------------------------------------------------------------------------
  // UI State Functions
  // ---------------------------------------------------------------------------

  /**
   * Whether the UI is in minimized state (FAB only visible)
   */
  function isUIMinimized(): boolean {
    if (state.value === "recording" && !recorderVisible.value) return true;
    if (state.value === "review_ready" && minimized.value) return true;
    if (state.value === "processing" && minimized.value) return true;
    return false;
  }

  /**
   * Minimize the view (soft touch - keeps state intact)
   */
  function minimize(): void {
    minimized.value = true;
  }

  /**
   * Restore the view from minimized state
   */
  function restore(): void {
    minimized.value = false;
  }

  /**
   * Set display mode (widget or modal)
   */
  function setDisplayMode(mode: "widget" | "modal"): void {
    displayMode.value = mode;
  }

  /**
   * Set recorder visibility
   */
  function setRecorderVisible(visible: boolean): void {
    recorderVisible.value = visible;
  }

  // ---------------------------------------------------------------------------
  // Return Public API
  // ---------------------------------------------------------------------------

  return {
    // State (as getters for clean usage without .value)
    get state() {
      return state.value;
    },
    get sessionData() {
      return sessionData.value;
    },
    get templates() {
      return templates.value;
    },
    get recorderUrl() {
      return recorderUrl.value;
    },
    get errorMessage() {
      return errorMessage.value;
    },
    get lockedSessionId() {
      return lockedSessionId.value;
    },
    get history() {
      return history.value;
    },

    // UI State (as getters)
    get displayMode() {
      return displayMode.value;
    },
    get minimized() {
      return minimized.value;
    },
    get recorderVisible() {
      return recorderVisible.value;
    },
    get deviceRecordingActive() {
      return deviceRecordingActive.value;
    },
    get isRecoveredSession() {
      return isRecoveredSession.value;
    },

    // Computed (as getters)
    get sessionId() {
      return sessionData.value?.recorderSessionId || sessionData.value?.sessionId || null;
    },
    get showFab() {
      // Hide FAB only during modal dialogs (form_template/device selection)
      return !["form_template_selection", "device_selection"].includes(state.value);
    },
    get isUIMinimized() {
      return isUIMinimized();
    },

    // Core Functions
    send,
    isIn,
    handleRecorderStatus,

    // Data Setters
    setTemplates,
    updateSessionData,
    setSummarySaving,

    // Data Getters
    getSummarySaving,

    // Feature flags
    // UI State Functions
    minimize,
    restore,
    setDisplayMode,
    setRecorderVisible,

    // Session Lock (exposed for backward compatibility)
    lockSession,
    unlockSession,
    shouldBlockSessionCreation,

    // Utilities
    clearHistory,
    reset,
    canTransition,

    // Refs for watchers (need reactivity)
    stateRef: readonly(state),
    sessionDataRef: readonly(sessionData),
    displayModeRef: readonly(displayMode),
    minimizedRef: readonly(minimized),
    recorderVisibleRef: readonly(recorderVisible),
    deviceRecordingActiveRef: readonly(deviceRecordingActive),
    recorderOpenedInTabRef: readonly(recorderOpenedInTab),
  };
}
